import LogoHorizontal from '@/icons/logo-horizontal.svg'
import Squares from '@/icons/squares-flattened.svg'
import Fingerprint from '@/icons/fingerprint.svg'
import HelpFingerprint from '@/icons/printbg-fingerprint-help.svg'
import RichText from '@/components/ui/rich-text'
import '../../css/print-wrapper.css'
import { cx } from '@/utils/strings'
import { useTranslations } from '@/utils/locale'
import { useEffect } from 'react'
import { hideKetchBannerInPrintMode } from '@/utils/print'

export interface WrapperProps {
  title?: string
  description?: any
  section?: string
  thirdPartyLogo?: any
  pageUrl?: string
  tagline?: string
  coverImage?: any
  pageType?: string
  pageBreadcrumb?: object[]
  orientation?: string
  securityPerspectives: boolean
}

export default function PrintWrapper({
  title = '',
  description = '',
  tagline,
  section,
  children,
  thirdPartyLogo,
  pageUrl,
  coverImage,
  pageType,
  pageBreadcrumb,
  orientation = 'portrait',
  securityPerspectives = false,
}: WrapperProps & { children: React.ReactNode }): JSX.Element {
  const { t } = useTranslations()
  const descStyle = 'relative ml-1 mt-1 mb-1 text-white z-10 text-[21px] [&*]:(text-white! font-normal! text-[21px])'

  useEffect(() => {
     hideKetchBannerInPrintMode()
  }, [])

  const renderSectionText = (sectionText: string) => (
    <h2
      className={cx(
        'relative! text-base! ml-1! text-white font-normal! z-10',
        !pageType && 'uppercase tracking-[0.3em]'
      )}
    >
      {sectionText}
    </h2>
  )

  const renderDescriptionText = (descriptionText: any) =>
    typeof descriptionText === 'string' ? (
      <p className={descStyle}>{descriptionText}</p>
    ) : (
      <div className={descStyle}>
        <RichText body={descriptionText} />
      </div>
    )

  const helpCover = () => (
    <div className="font-body bg-white max-w-7xl mx-auto h-[calc(100vh)] [page-break-after: always]">
      <div className="p-16 bg-secondary-100 overflow-hidden relative h-full">
        <div className="h-[calc(13vh)] fill-current absolute top-0 left-0 right-0 text-white bg-indigoBlue flex items-center z-10 pl-10">
          <div className="flex flex-row items-center">
            <LogoHorizontal className="w-[174px] flex-[1 0 auto] mr-8" />
            <div className="text-[17pt] border-l-[1pt] border-white pl-8 pt-[3px] mt-[2px]">
              {t('Help Center Article')}
            </div>
          </div>
        </div>
        <div className="absolute top-[20%] px-10 left-0 right-0 z-10">
          {pageBreadcrumb?.length > 0 && (
            <div className="uppercase text-indigoBlue text-[15pt] flex items-center">
              {pageBreadcrumb
                .map((nav) => nav.title)
                .reduce((prev, curr, index): any => [
                  prev,
                  <span key={index} className="font-bwi mx-[10px] text-[9pt]">
                    &#xe91b;
                  </span>,
                  curr,
                ])}
            </div>
          )}
          <h1 className="relative! font-normal! leading-[50px] text-[50px] text-indigoBlue mt-5!">{title}</h1>
        </div>
        <div className="absolute left-0 right-0 bottom-32 z-5 h-[55vh]">
          <div className="relative z-10 h-full [svg]:absolute [svg]:top-0 [svg]:bottom-0 [svg]:z-0 [svg]:h-full [svg]:w-full">
            <div className="fingerprint-gradient-overlay absolute left-0 right-0 top-0 z-10 bottom-0" />
            <HelpFingerprint className="fill-none!" />
          </div>
        </div>
        <div className="h-[calc(13vh)] absolute bottom-0 left-0 right-0 text-white bg-indigoBlue z-10">
          <div className="flex items-center h-full pl-10 text-left">
            <div>
              {t('View in the help center')}: <br />
              <a href={pageUrl} className="text-white! block">
                {pageUrl}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  const table = (
    <table className="p-0 w-full">
      {/* create space for header */}
      <thead>
        <tr>
          <td className="p-0">
            <div className="h-[50px]" />
          </td>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td className="p-0">
            <div>
              {/* specific x padding to match header and footer of pdf generation */}
              <article id="print-body">{children}</article>
            </div>
          </td>
        </tr>
      </tbody>
      {/* create space for footer */}
      <tfoot>
        <tr>
          <td className="p-0">
            <div className="h-[50px]" />
          </td>
        </tr>
      </tfoot>
    </table>
  )

  if (securityPerspectives) {
    return (
      <>
        <div className="font-body bg-white max-w-7xl mx-auto h-[calc(100vh)] [page-break-after: always]">
          <div className="p-14 bg-primaryBlue relative h-[100px] rounded-b-4xl relative z-10">
            <div className="text-white fill-current flex items-center z-10">
              <LogoHorizontal className={cx('w-48 flex-[1 0 auto]', thirdPartyLogo && 'border-r-2 pr-7 py-2 mr-7')} />
            </div>
          </div>
          <div className="h-[25px]" />
          <div
            className="h-[488px] bg-white rounded-4xl"
            style={{
              backgroundImage: coverImage?.fields.file.url ? `url(${coverImage.fields.file.url})` : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
          <div className="h-[25px]" />
          <div className="h-[calc(100vh-650px)] bg-primaryBlue rounded-t-4xl px-16 pt-12">
            <h2 className="tracking-[0.2em] text-base! text-white font-normal! uppercase">
              Bitwarden Security Perspectives
            </h2>

            <h1 className="relative! font-normal! leading-[40px] text-[40px] text-white z-10! w-[80%] mt-0!">{title}</h1>
            {description && renderDescriptionText(description)}
          </div>
        </div>
        {table}
      </>
    )
  } else if (pageType === 'help') {
    return (
      <>
        {helpCover()}
        {table}
      </>
    )
  } else {
    return (
      <>
        <div className="font-body bg-white max-w-7xl mx-auto h-[calc(100vh)] [page-break-after: always]">
          {orientation === 'landscape' && <style>{`@media print {@page {size: landscape}}`}</style>}
          <div className="p-14 bg-indigoBlue overflow-hidden relative h-full">
            {!pageType && renderSectionText(section)}
            {pageType === 'fingerprint' && tagline && (
              <h2 className="tracking-[0.1em] text-lg! text-white my-8 font-normal! uppercase">{tagline}</h2>
            )}
            <h1 className="relative! font-normal! leading-[60px] text-[60px] text-white myb-8! z-10!">{title}</h1>
            {description && renderDescriptionText(description)}
            {pageUrl && (
              <h2 className="relative! ml-1! pt-5 text-white font-normal! z-10 text-[18px]">
                {t('Get the full interactive view at')}
                <br /> <a href={pageUrl} className="text-white! block">{pageUrl}</a>
              </h2>
            )}
            {!coverImage && (
              <div className="absolute right-0 bottom-10 h-[66vh] [svg]:h-full [svg]:w-auto text-primaryBlue z-1 translate-x-[50%]">
                <Squares />
              </div>
            )}
            {coverImage === 'fingerprint' && (
              <div className="absolute right-0 bottom-0 h-[500px] w-[460px] [svg]:w-full [svg]:h-full z-1">
                <Fingerprint />
              </div>
            )}
            <div className="absolute bottom-10 text-white fill-current flex items-center z-10">
              <LogoHorizontal className={cx('w-48 flex-[1 0 auto]', thirdPartyLogo && 'border-r-2 pr-7 py-2 mr-7')} />

              {thirdPartyLogo && (
                <img
                  src={thirdPartyLogo.file.url}
                  alt={thirdPartyLogo.title}
                  className="max-h-20 max-w-[12rem] py-2 object-contain"
                />
              )}
            </div>
          </div>
        </div>
        {table}
      </>
    )
  }
}
